/*
 * Jersey Configurator (theme section) — drives tabs/dropdowns/preview, variant swap,
 * and the _TRIKOT bundle add-to-cart on the PDP metafield/ERP model
 */
(function () {
  'use strict';

  function money(cents, fmt) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, fmt || window.moneyFormat);
      }
    } catch (e) { }
    return (Number(cents) / 100).toFixed(2) + ' CHF';
  }
  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ---------- personalization hand-off between jerseys ----------
   * Switching jersey re-renders the whole section (Section Rendering API) and re-inits it
   * from scratch — or, when that fetch fails, navigates to the sibling's product page. Both
   * paths used to drop whatever was typed. So the entered personalization is parked in
   * sessionStorage on the way out and picked up by whichever UI renders next (this section
   * or the PDP block, which reads the same key). Read-and-delete: a one-shot hand-off, so a
   * name can never resurrect on some later unrelated visit.
   */
  var CARRY_KEY = 'evz:jersey:carry';
  function saveCarry(data) {
    try { window.sessionStorage.setItem(CARRY_KEY, JSON.stringify(data)); } catch (e) { }
  }
  function takeCarry() {
    try {
      var raw = window.sessionStorage.getItem(CARRY_KEY);
      window.sessionStorage.removeItem(CARRY_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  // The roster is a per-jersey hidden product, so a player's variant id from the previous
  // jersey means nothing here — carry the identity (name + number) and re-resolve it.
  function normName(v) {
    return String(v == null ? '' : v).toLowerCase().replace(/[.,'’`\-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  }
  function digitsOnly(v) { return String(v == null ? '' : v).replace(/\D/g, ''); }

  // The flocked name reaches Conte Sports exactly as stored (FLOCK / Individuell - Name), so
  // store it the way it is printed: uppercase. de-CH maps ä/ö/ü -> Ä/Ö/Ü and keeps i -> I
  // (no Turkish dotted İ); ß -> SS, the Swiss spelling. Digits, spaces, hyphens and
  // apostrophes are left as they are. Same helper in jersey-personalization.js.
  function flockCase(v) {
    v = String(v == null ? '' : v);
    if (v.normalize) v = v.normalize('NFC'); // pasted from a Mac: u + combining ¨ -> one ü
    try { return v.toLocaleUpperCase('de-CH'); } catch (e) { return v.toUpperCase(); }
  }
  // Uppercase a name field in place (typing, paste, autofill), keeping the caret put.
  function upcaseField(el) {
    var v = el.value, up = flockCase(v);
    if (up === v) return;
    var pos = el.selectionStart;
    el.value = up;
    if (pos != null && document.activeElement === el) {
      var p = flockCase(v.slice(0, pos)).length;
      try { el.setSelectionRange(p, p); } catch (e) { }
    }
  }

  function init(root) {
    if (root.hasAttribute('data-jc-initialized')) return;
    root.setAttribute('data-jc-initialized', 'true');

    var cfgEl = root.querySelector('[data-jc-config]');
    if (!cfgEl) return;
    var config;
    try { config = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    config.numbers = config.numbers || {};
    var fmt = config.moneyFormat || 'CHF {{amount}}';
    var strings = config.strings || {};
    function s(k, f) { return strings[k] || f; }

    var state = {
      mode: 'none',
      player: null,           // { variantId, name, number, amount, sku }
      customName: '',
      customNumber: '',
      variantId: null,        // selected SIZE variant
      variantPrice: Number(config.initialVariantPrice) || 0,
      sizeSelected: false,    // has the shopper actually picked a size yet?
      busy: false
    };

    // ---------- elements ----------
    var tabs = root.querySelectorAll('.personalization-tab');
    var playerContainer = root.querySelector('[data-jc-player-container]');
    var customInputs = root.querySelector('[data-jc-custom-inputs]');
    var nameInput = root.querySelector('[data-jc-name]');
    var numberInput = root.querySelector('[data-jc-number]');
    var customError = root.querySelector('[data-jc-custom-error]');
    var persoPrice = root.querySelector('[data-jc-perso-price]');
    var selWrap = root.querySelector('[data-jc-select-wrapper]');
    var selTrigger = root.querySelector('[data-jc-select-trigger]');
    var selValue = root.querySelector('[data-jc-select-value]');
    var selOptions = root.querySelector('[data-jc-select-options]');
    var search = root.querySelector('[data-jc-search]');
    var defaultPlayerLabel = selValue ? selValue.textContent : s('selectPlayer', 'Spieler auswählen...');
    var sizeWrap = root.querySelector('[data-jc-size-wrapper]');
    var sizeTrigger = root.querySelector('[data-jc-size-trigger]');
    var sizeValue = root.querySelector('[data-jc-size-value]');
    var sizeOptions = root.querySelector('[data-jc-size-options]');
    var oosMsg = root.querySelector('[data-jc-oos]');
    var totalEl = root.querySelector('[data-jc-total]');
    var submit = root.querySelector('[data-jc-submit]');
    var messages = root.querySelector('[data-jc-messages]');
    // The message box is rendered after the product description, far below the fold — so a
    // validation error (e.g. "Bitte Grösse wählen") appeared where nobody looks. Move it
    // right under the add-to-cart button (inline styles survive the class swap in showMsg).
    if (submit && messages && submit.parentNode) {
      submit.parentNode.appendChild(messages);
      messages.style.marginTop = '10px';
      messages.style.textAlign = 'center';
    }
    var flip = root.querySelector('[data-jc-flip]');
    var pvName = root.querySelector('[data-jc-preview-name]');
    var pvNumber = root.querySelector('[data-jc-preview-number]');
    var pvFrontNumber = root.querySelector('[data-jc-preview-front-number]');

    // ---------- number (whole-number Ziffer variant) ----------
    function sanitizeNumber(n) { return String(n || '').replace(/\D/g, '').slice(0, 2); }
    function numberInfo(n) {
      n = sanitizeNumber(n);
      if (!n) return null;
      return config.numbers[n] || config.numbers[String(parseInt(n, 10))] || null;
    }
    function numberIssue(n) {
      if (!sanitizeNumber(n)) return '';
      var info = numberInfo(n);
      if (!info || info.available === false) return s('errorNumberStock', 'Diese Nummer ist nicht verfügbar.');
      return '';
    }

    // Has the shopper actually personalized anything? Picking the "Wunschname" tab alone
    // is not a personalization — the jersey is still identical to a Blanko one, so it must
    // stay at the base price until a name OR a number is typed (matches the add-to-cart
    // validation, which rejects an empty custom selection).
    function isPersonalized() {
      if (state.mode === 'player') return !!state.player;
      if (state.mode === 'custom') return !!(state.customName.trim() || state.customNumber.trim());
      return false;
    }

    // Add-on cost: only what's actually picked/entered, so the total only ever rises
    function addonCents() {
      if (state.mode === 'player') return state.player ? Number(state.player.amount) || 0 : 0;
      if (state.mode === 'custom') {
        var t = 0;
        // Name is always charged: typed name, else the "no name" placeholder (flat CHF 20).
        if (state.customName.trim() && config.nameAddon) t += Number(config.nameAddon.price) || 0;
        else if (config.noNameAddon) t += Number(config.noNameAddon.price) || 0;
        // Number is always charged: typed digit, else the "no number" placeholder.
        var ni = numberInfo(state.customNumber);
        if (ni) t += Number(ni.price) || 0;
        else if (config.noNumberAddon) t += Number(config.noNumberAddon.price) || 0;
        return t;
      }
      return 0;
    }

    // ---------- price ----------
    // Sizes can carry different prices. Until the shopper picks one, we can't show a single
    // exact total — so show the cheapest size prefixed with "Ab"/"From". Once a size is
    // selected the prefix drops and the real variant price is shown.
    var priceVaries = !!config.priceVaries;
    var priceMin = Number(config.priceMin) || state.variantPrice;
    var fromPrefix = s('priceFromPrefix', '');
    function applyPrice() {
      var a = isPersonalized() ? addonCents() : 0;
      var showFrom = priceVaries && !state.sizeSelected;
      var base = showFrom ? priceMin : state.variantPrice;
      var text = money(base + a, fmt);
      if (showFrom && fromPrefix) text = fromPrefix + ' ' + text;
      if (totalEl) totalEl.textContent = text;
      if (persoPrice) persoPrice.textContent = a ? '(+' + money(a, fmt) + ')' : '';
    }

    // ---------- preview ----------
    function setView(v) {
      if (flip) flip.classList.toggle('flipped', v === 'back');
      root.querySelectorAll('[data-jc-view]').forEach(function (b) {
        b.classList.toggle('active', b.getAttribute('data-jc-view') === v);
      });
    }
    function nameSizeClass(len) {
      pvName.classList.remove('preview-name-medium', 'preview-name-long', 'preview-name-very-long');
      if (len > 12) pvName.classList.add('preview-name-very-long');
      else if (len > 9) pvName.classList.add('preview-name-long');
      else if (len > 6) pvName.classList.add('preview-name-medium');
    }
    // Whichever face THIS jersey prints in (retro keeps the old bubble font — see
    // .jc-retro in the CSS). The gate in opticalShiftEm must name the font actually
    // rendered, otherwise it reads "not loaded" forever and the shift silently stays 0.
    var isRetro = root.classList.contains('jc-retro');
    var printFont = isRetro
      ? "1em 'EVZ Retro Bubble'"
      : "1em 'Beachwood Tall Semibold'";
    // Retro only: the bubble face runs far wider per glyph than the Beachwood the other
    // jerseys use, and the length steps above were calibrated for Beachwood — so "MOVERARE"
    // drew 1.4x the width of the jersey body and spilled over the shoulders. Measure the
    // name as actually drawn and scale it down until it fits. The cap is 1.10x the crest
    // circle on the retro back photo (circle measured at 30.2% of the photo width => 33%).
    // Non-retro jerseys never enter this function, so --jc-name-fit stays unset for them
    // and the CSS `* var(--jc-name-fit, 1)` leaves their size byte-identical.
    var RETRO_NAME_MAX = 0.33;
    function fitRetroName() {
      if (!isRetro || !pvName) return;
      pvName.style.removeProperty('--jc-name-fit');
      if (!pvName.textContent) return;
      var host = pvName.parentNode ? pvName.parentNode.getBoundingClientRect().width : 0;
      if (!host) return;
      var rng = document.createRange();
      rng.selectNodeContents(pvName);
      var w = rng.getBoundingClientRect().width;
      if (w > host * RETRO_NAME_MAX) {
        pvName.style.setProperty('--jc-name-fit', ((host * RETRO_NAME_MAX) / w).toFixed(4));
      }
      // font-display:swap means the first measure can land on the fallback face; redo it
      // once the real glyphs exist. check() is true afterwards, so this cannot loop.
      if (document.fonts && document.fonts.load && !document.fonts.check(printFont)) {
        document.fonts.load(printFont).then(fitRetroName).catch(function () { });
      }
    }
    // Optical centring: a geometrically-centred number can still read off-centre because
    // its ink weight is uneven (e.g. "14" leans right, "88" is balanced). Returns the shift
    // in EM (font-relative, from an offscreen canvas — no live width needed) so the number's
    // ink centre-of-mass, not its bounding box, sits on the circle centre.
    var _octx = null;
    function opticalShiftEm(el, text) {
      if (!text) return 0;
      try {
        if (document.fonts && !document.fonts.check(printFont)) return 0;
        if (!_octx) { var oc = document.createElement('canvas'); oc.width = 1024; oc.height = 512; _octx = oc.getContext('2d', { willReadFrequently: true }); }
        var cs = getComputedStyle(el), SIZE = 256;
        _octx.clearRect(0, 0, 1024, 512);
        _octx.textBaseline = 'middle'; _octx.textAlign = 'center';
        _octx.font = (cs.fontWeight || '400') + ' ' + SIZE + 'px ' + (cs.fontFamily || 'sans-serif');
        _octx.fillStyle = '#000';
        _octx.fillText(text, 512, 256);
        var d = _octx.getImageData(0, 0, 1024, 512).data;
        var minX = 1e9, maxX = -1, sum = 0, wsum = 0;
        for (var y = 0; y < 512; y++) { var row = y * 1024; for (var x = 0; x < 1024; x++) { var a = d[(row + x) * 4 + 3]; if (a > 40) { if (x < minX) minX = x; if (x > maxX) maxX = x; sum += a; wsum += a * x; } } }
        if (maxX < 0 || !sum) return 0;
        var bbox = (minX + maxX) / 2, com = wsum / sum, inkW = (maxX - minX) || 1;
        // Only ever nudge LEFT. Right-heavy numbers (e.g. 14, 73) look shifted right and
        // need pulling left; left-heavy numbers (e.g. 37, 27) look fine geometrically centred
        // and pushing them right (as full mass-centring does) looks bad. So clamp to <= 0.
        return Math.min(0, -(((com - bbox) / inkW) * (inkW / SIZE)));
      } catch (e) { return 0; }
    }
    // The var is set on the element itself, so front and back can share the name.
    function applyNumberOptical(el, number) {
      if (!el) return;
      el.style.setProperty('--jc-number-optical', (number ? opticalShiftEm(el, number).toFixed(4) : '0') + 'em');
      // The bubble font loads on demand; recompute for the current number once it resolves.
      if (number && document.fonts && document.fonts.load) {
        document.fonts.load(printFont).then(function () {
          if (el.textContent === number) el.style.setProperty('--jc-number-optical', opticalShiftEm(el, number).toFixed(4) + 'em');
        }).catch(function () { });
      }
    }

    // Update overlay text + price only; view is driven by the tab, centring by CSS
    function refreshPreview() {
      var name = '', number = '';
      if (state.mode === 'player' && state.player) { name = state.player.name; number = state.player.number; }
      else if (state.mode === 'custom') { name = state.customName.trim(); number = state.customNumber.trim(); }
      if (pvName) { pvName.textContent = (name || '').toUpperCase(); nameSizeClass((name || '').length); fitRetroName(); }
      if (pvNumber) { pvNumber.textContent = number || ''; applyNumberOptical(pvNumber, number || ''); }
      // Front carries the number only — no name.
      if (pvFrontNumber) { pvFrontNumber.textContent = number || ''; applyNumberOptical(pvFrontNumber, number || ''); }
      applyPrice();
    }

    // ---------- tabs ----------
    function setMode(mode) {
      state.mode = mode;
      for (var i = 0; i < tabs.length; i++) tabs[i].classList.toggle('active', tabs[i].getAttribute('data-mode') === mode);
      if (playerContainer) playerContainer.classList.toggle('hidden', mode !== 'player');
      if (customInputs) customInputs.classList.toggle('hidden', mode !== 'custom');
      if (mode === 'none') {
        state.player = null; state.customName = ''; state.customNumber = '';
        if (nameInput) nameInput.value = ''; if (numberInput) numberInput.value = '';
        resetPlayer();
      } else if (mode === 'player') {
        state.customName = ''; state.customNumber = '';
        if (nameInput) nameInput.value = ''; if (numberInput) numberInput.value = '';
      } else if (mode === 'custom') {
        state.player = null; resetPlayer();
      }
      setCustomError('');
      setView(mode === 'none' ? 'front' : 'back');
      refreshPreview();
    }
    for (var t = 0; t < tabs.length; t++) {
      (function (tab) { tab.addEventListener('click', function () { setMode(tab.getAttribute('data-mode')); }); })(tabs[t]);
    }

    // ---------- player dropdown ----------
    // The roster product's variant order is whatever the ERP pushed, so the list came out
    // unsorted and differed per jersey. Order it by jersey number, low to high; entries
    // without a usable number keep their roster order at the end.
    (function sortPlayerOptions() {
      if (!selOptions) return;
      var rows = [];
      selOptions.querySelectorAll('.custom-select-option').forEach(function (o, i) {
        var n = parseInt((o.getAttribute('data-number') || '').replace(/\D/g, ''), 10);
        rows.push({ el: o, no: isNaN(n) ? 1e9 : n, i: i });
      });
      rows.sort(function (a, b) { return a.no - b.no || a.i - b.i; });
      rows.forEach(function (r) { selOptions.appendChild(r.el); });
    })();
    function resetPlayer() {
      if (selValue) selValue.textContent = defaultPlayerLabel;
      if (selWrap) selWrap.classList.remove('open');
      if (selOptions) selOptions.querySelectorAll('.custom-select-option').forEach(function (o) { o.classList.remove('selected'); });
    }
    // Only one dropdown may stand open at a time. Every trigger stopPropagation()s so that
    // its own click doesn't immediately reach the document handler below and re-close it —
    // which also means that handler never sees trigger clicks, so opening one dropdown can't
    // rely on it to close the others. Do it explicitly here. Selecting `.custom-select`
    // rather than a fixed list keeps this correct if another dropdown is added later.
    function closeOtherDropdowns(keep) {
      root.querySelectorAll('.custom-select.open').forEach(function (w) {
        if (w !== keep) w.classList.remove('open');
      });
    }
    if (selTrigger && selWrap) {
      selTrigger.addEventListener('click', function (e) {
        e.stopPropagation();
        closeOtherDropdowns(selWrap);
        selWrap.classList.toggle('open');
        if (selWrap.classList.contains('open') && search) {
          search.value = '';
          selOptions.querySelectorAll('.custom-select-option').forEach(function (o) { o.style.display = ''; });
          setTimeout(function () { search.focus(); }, 50);
        }
      });
    }
    if (search && selOptions) {
      search.addEventListener('click', function (e) { e.stopPropagation(); });
      search.addEventListener('input', function () {
        var q = search.value.toLowerCase();
        selOptions.querySelectorAll('.custom-select-option').forEach(function (o) {
          var txt = ((o.getAttribute('data-number') || '') + ' ' + (o.getAttribute('data-name') || '')).toLowerCase();
          o.style.display = txt.indexOf(q) > -1 ? '' : 'none';
        });
      });
    }
    // Single path into "a player is chosen", shared by the click handler and the restore
    // below, so a carried-over player lands in exactly the state a click would produce.
    function selectPlayerOption(opt) {
      if (!opt || opt.getAttribute('data-available') === 'false') return false;
      state.player = {
        variantId: opt.getAttribute('data-variantid'),
        // Roster names come from the ERP in mixed case ("Stadler") — store them as flocked.
        name: flockCase(opt.getAttribute('data-name')),
        number: (opt.getAttribute('data-number') || '').replace(/[^0-9]/g, ''),
        amount: Number(opt.getAttribute('data-amount')) || 0,
        sku: opt.getAttribute('data-sku') || ''
      };
      if (selValue) selValue.textContent = state.player.number + ' - ' + state.player.name.toUpperCase();
      if (selOptions) selOptions.querySelectorAll('.custom-select-option').forEach(function (o) { o.classList.remove('selected'); });
      opt.classList.add('selected');
      if (selWrap) selWrap.classList.remove('open');
      refreshPreview();
      return true;
    }
    if (selOptions) {
      selOptions.addEventListener('click', function (e) {
        if (e.target.closest('.custom-select-search')) { e.stopPropagation(); return; }
        var opt = e.target.closest('.custom-select-option');
        if (!opt) return;
        if (opt.getAttribute('data-available') === 'false') { e.stopPropagation(); return; }
        selectPlayerOption(opt);
      });
    }

    // ---------- custom inputs ----------
    function setCustomError(m) { if (customError) { customError.textContent = m || ''; customError.style.display = m ? '' : 'none'; } }
    // Mid-composition (dead keys, Android keyboards) the field is left alone — rewriting it
    // there duplicates text — and compositionend / change uppercase it afterwards. The state
    // is uppercased either way, so preview and cart never see mixed case.
    function onNameInput(e) {
      if (!(e && e.isComposing)) upcaseField(nameInput);
      state.customName = flockCase(nameInput.value);
      refreshPreview();
    }
    if (nameInput) ['input', 'compositionend', 'change'].forEach(function (ev) { nameInput.addEventListener(ev, onNameInput); });
    if (numberInput) numberInput.addEventListener('input', function () {
      var c = sanitizeNumber(numberInput.value);
      if (c !== numberInput.value) numberInput.value = c;
      state.customNumber = c;
      setCustomError(numberIssue(c));
      refreshPreview();
    });

    // ---------- size dropdown ----------
    function selectSize(opt) {
      if (!opt || opt.getAttribute('data-available') === 'false') return;
      state.variantId = opt.getAttribute('data-variantid');
      state.variantPrice = Number(opt.getAttribute('data-price')) || state.variantPrice;
      state.sizeSelected = true;
      if (sizeValue) sizeValue.textContent = (opt.querySelector('.size-name') || {}).textContent || sizeValue.textContent;
      sizeOptions.querySelectorAll('.custom-select-option').forEach(function (o) { o.classList.remove('selected'); });
      opt.classList.add('selected');
      if (sizeWrap) sizeWrap.classList.remove('open');
      if (oosMsg) oosMsg.classList.add('hidden');
      if (submit) submit.disabled = false;
      applyPrice();
    }
    if (sizeTrigger && sizeWrap) sizeTrigger.addEventListener('click', function (e) {
      e.stopPropagation();
      closeOtherDropdowns(sizeWrap);
      sizeWrap.classList.toggle('open');
    });
    if (sizeOptions) sizeOptions.addEventListener('click', function (e) {
      var opt = e.target.closest('.custom-select-option');
      if (opt) selectSize(opt);
    });

    document.addEventListener('click', function (e) {
      if (selWrap && !selWrap.contains(e.target)) selWrap.classList.remove('open');
      if (sizeWrap && !sizeWrap.contains(e.target)) sizeWrap.classList.remove('open');
    });

    // ---------- view toggle + description ----------
    root.querySelectorAll('[data-jc-view]').forEach(function (b) {
      b.addEventListener('click', function () { setView(b.getAttribute('data-jc-view')); });
    });
    var descToggle = root.querySelector('[data-jc-desc-toggle]');
    var descSection = root.querySelector('.product-description-section');
    if (descToggle && descSection) descToggle.addEventListener('click', function () {
      descSection.classList.toggle('open');
    });

    // Descriptions are pasted from Word/Outlook and Shopify stores that markup
    // verbatim: <meta> and <style> tags plus inline font declarations. A <style>
    // that lands in the body still applies document-wide — the pasted rules
    // restyle every link on the page — so the nodes have to go, not just be
    // hidden. The typography overrides live in the stylesheet; here we drop the
    // properties at the source so nothing has to be fought with !important.
    var descContent = root.querySelector('[data-jc-desc-content]');
    if (descContent) {
      descContent.querySelectorAll('style, meta, link, title').forEach(function (n) {
        n.remove();
      });
      descContent.querySelectorAll('[style]').forEach(function (n) {
        ['font-family', 'font-size', 'line-height', 'background', 'background-color'].forEach(function (p) {
          n.style.removeProperty(p);
        });
        if (!n.getAttribute('style')) n.removeAttribute('style');
      });
    }

    // ---------- carry the personalization across a jersey switch ----------
    // Only identity travels: variant ids / prices / SKUs belong to the jersey being left
    // and are re-resolved against the new jersey's own add-on products.
    function snapshot() {
      return {
        mode: state.mode,
        name: state.customName,
        number: state.customNumber,
        player: state.player ? { name: state.player.name, number: state.player.number } : null
      };
    }
    function hasTab(mode) {
      for (var i = 0; i < tabs.length; i++) {
        if (tabs[i].getAttribute('data-mode') === mode) return true;
      }
      return false;
    }
    // Same player on the new jersey: match on name, and prefer the entry that also carries
    // the same number. Never match on number alone — if this roster gives #18 to somebody
    // else, that would silently put a different player's name on the shopper's jersey.
    function findPlayerOption(p) {
      if (!selOptions || !p) return null;
      var wantName = normName(p.name);
      if (!wantName) return null;
      var wantNo = digitsOnly(p.number);
      var opts = selOptions.querySelectorAll('.custom-select-option');
      var byName = null;
      for (var i = 0; i < opts.length; i++) {
        var o = opts[i];
        if (o.getAttribute('data-available') === 'false') continue;
        if (normName(o.getAttribute('data-name')) !== wantName) continue;
        if (wantNo && digitsOnly(o.getAttribute('data-number')) === wantNo) return o;
        if (!byName) byName = o;
      }
      return byName;
    }
    // Initial state: what was carried in, else Blanko (the previous default).
    function restore(c) {
      var mode = c && (c.mode === 'player' || c.mode === 'custom') ? c.mode : 'none';
      // A jersey without that add-on product has no such tab — fall back to Blanko.
      if (!hasTab(mode)) mode = 'none';
      setMode(mode); // clears inputs, sets tab/containers/view, then we fill them
      if (mode === 'custom') {
        if (nameInput && c.name) {
          var max = parseInt(nameInput.getAttribute('maxlength'), 10) || 0;
          nameInput.value = flockCase(max > 0 ? String(c.name).slice(0, max) : String(c.name));
          state.customName = nameInput.value;
        }
        if (numberInput && c.number) {
          var n = sanitizeNumber(c.number);
          numberInput.value = n;
          state.customNumber = n;
          // This jersey has its own Ziffer variants: flag an unavailable number right away,
          // exactly as typing it would (add-to-cart rejects it on the same check).
          setCustomError(numberIssue(n));
        }
        refreshPreview();
      } else if (mode === 'player' && c.player) {
        // No match (not on this roster, or sold out here) leaves the dropdown on its
        // placeholder, so the shopper sees they have to pick again instead of the wrong name.
        selectPlayerOption(findPlayerOption(c.player));
      }
    }

    // ---------- variant switch (Section Rendering API) ----------
    root.querySelectorAll('[data-jc-variant-handle]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        if (a.classList.contains('active')) { e.preventDefault(); return; }
        var handle = a.getAttribute('data-jc-variant-handle');
        // Fetch the sibling's product URL so the global product drives the render
        // (Liquid can't read query params); fall back to full navigation
        var fallback = a.getAttribute('href');
        // Hand the personalization to whatever renders next — the swapped-in section below,
        // or the sibling's PDP when we fall through / fall back to a real navigation.
        saveCarry(snapshot());
        if (!handle || !config.sectionId) return;
        e.preventDefault();
        var fetchUrl = cartRoot() + 'products/' + encodeURIComponent(handle) +
          '?section_id=' + encodeURIComponent(config.sectionId);
        fetch(fetchUrl).then(function (r) { return r.text(); }).then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-jc]');
          if (!fresh) { location.href = fallback; return; }
          root.replaceWith(fresh);
          init(fresh);
        }).catch(function () { location.href = fallback; });
      });
    });

    // ---------- add to cart ----------
    function jerseyTypeValue() {
      var v = (config.version || '').toString().trim();
      return v ? v.charAt(0).toUpperCase() + v.slice(1) : '';
    }
    function genderValue() {
      var g = (config.gender || '').toString().toLowerCase();
      if (!g) return '';
      if (g.indexOf('frau') > -1 || g.indexOf('women') > -1 || g.indexOf('female') > -1) return 'Frauen';
      if (g.indexOf('kind') > -1 || g.indexOf('kid') > -1) return 'Kinder';
      if (g.indexOf('herr') > -1 || g.indexOf('männ') > -1 || g.indexOf('mann') > -1 || g.indexOf('men') > -1) return 'Männer';
      return config.gender;
    }
    function buildItems(trikotId, variantId) {
      var items = [];
      var name = '', number = '';
      if (state.mode === 'player' && state.player) { name = state.player.name; number = state.player.number; }
      else if (state.mode === 'custom') { name = state.customName.trim(); number = state.customNumber.trim(); }
      name = flockCase(name); // last line of defence: every FLOCK / Individuell - Name below is uppercase
      var flockStr = number && name ? number + ' ' + name : number || name;

      if (state.mode === 'player' && state.player) {
        var p = state.player, pp = { _TRIKOT: trikotId, _bundle_type: 'child' };
        if (p.sku) pp.SKU = p.sku;
        var pn = numberInfo(p.number);
        if (pn && pn.sku) pp.SKU_Nummer = pn.sku;
        if (flockStr) pp.FLOCK = flockStr;
        items.push({ id: p.variantId, quantity: 1, properties: pp });
      } else if (state.mode === 'custom') {
        // NAME line: typed name (real variant), else the "no name" placeholder so the CHF 10
        // is always charged. `_placeholder` is private (hidden) + lets the cart label it.
        if (name && config.nameAddon) {
          var np = { _TRIKOT: trikotId, _bundle_type: 'child' };
          if (config.nameAddon.sku) np.SKU = config.nameAddon.sku;
          np.FLOCK = name;
          items.push({ id: config.nameAddon.variantId, quantity: 1, properties: np });
        } else if (config.noNameAddon) {
          var npn = { _TRIKOT: trikotId, _bundle_type: 'child', _placeholder: 'name' };
          if (config.noNameAddon.sku) npn.SKU = config.noNameAddon.sku;
          items.push({ id: config.noNameAddon.variantId, quantity: 1, properties: npn });
        }
        // NUMBER line: typed digit, else the "no number" placeholder.
        var ni = numberInfo(number);
        if (ni) {
          var dp = { _TRIKOT: trikotId, _bundle_type: 'child' };
          if (ni.sku) dp.SKU = ni.sku;
          dp.FLOCK = number;
          items.push({ id: ni.variantId, quantity: 1, properties: dp });
        } else if (config.noNumberAddon) {
          var dpn = { _TRIKOT: trikotId, _bundle_type: 'child', _placeholder: 'number' };
          if (config.noNumberAddon.sku) dpn.SKU = config.noNumberAddon.sku;
          items.push({ id: config.noNumberAddon.variantId, quantity: 1, properties: dpn });
        }
      }

      var mp = { _TRIKOT: trikotId };
      if (state.mode === 'custom') { if (number) mp['Individuell - Nummer'] = number; if (name) mp['Individuell - Name'] = name; }
      else if (flockStr) { mp.FLOCK = flockStr; } // player mode only; custom shows Individuell-Nummer/Name instead
      mp._bundle_type = 'main';
      var jt = jerseyTypeValue(); if (jt) mp._jersey_type = jt;
      var gv = genderValue(); if (gv) mp._gender = gv;
      items.push({ id: variantId, quantity: 1, properties: mp });
      return items;
    }
    function showMsg(text, isError) {
      if (!messages) return;
      messages.textContent = text;
      messages.className = isError ? 'error-message' : 'info-text';
      // Bring the error (and the button it sits under) into view so it can't be missed.
      if (isError && text) {
        try { (submit || messages).scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { }
      }
    }
    function refreshCart() {
      var $ = window.jQuery;
      if ($) { var trg = $('.openCartDrawer').first(); if (trg.length) { trg.trigger('click'); return; } }
      window.location.href = cartRoot() + 'cart';
    }
    if (submit) submit.addEventListener('click', function () {
      if (state.busy) return;
      if (!state.variantId) { showMsg('Bitte Grösse wählen.', true); return; }
      if (state.mode === 'player' && !state.player) { showMsg('Bitte Spieler auswählen.', true); return; }
      if (state.mode === 'custom') {
        var n = state.customName.trim(), num = state.customNumber.trim();
        if (!n && !num) { showMsg('Bitte Name oder Nummer eingeben.', true); return; }
        var issue = numberIssue(num);
        if (issue) { setCustomError(issue); showMsg(issue, true); return; }
      }
      state.busy = true; submit.disabled = true;
      var trikotId = String(Math.floor(Math.random() * 100000000));
      var items = buildItems(trikotId, state.variantId);
      fetch(cartRoot() + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (d) { throw new Error((d && d.description) || s('errorCouldNotAdd', 'Could not add to cart')); });
        return r.json();
      }).then(function () { refreshCart(); }).catch(function (err) {
        showMsg((err && err.message) || s('errorAddToCart', 'Fehler beim Hinzufügen zum Warenkorb.'), true);
      }).then(function () { state.busy = false; submit.disabled = false; });
    });

    // default
    restore(takeCarry());
    applyPrice();
  }

  function initAll() { document.querySelectorAll('[data-jc]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
